import React, { useRef, useCallback, useState } from 'react';
import { Circle, Group, Stage, Layer, Rect, Line} from 'react-konva';
import {getStepFromPosition} from '../getStepFromPosition.js';
import { connect } from "react-redux";
import {DEFAULT_RESOLUTION} from '../constants.js';
import VolumeAutomationPoint from './VolumeAutomationPoint.js';
import VolumeGuidePoint from './VolumeGuidePoint.js';
import {
    setArrangementCursorStep,
    setArrangementMouseX,
    setArrangementRowSelections,
       } from 'actions/index.js';
import {
    SUB_SEQUENCE_VOLUME_CHANGE
} from 'session/constants.js';
import SubSequenceVolumeChangeEvent from 'session/SubSequenceVolumeChangeEvent.js';
import {
    HEIGHT,
    getValueFromYPosition,
    getXPositionFromStep,
    getYPositionFromVolume
} from './positionUtils.js';

function VolumeChangeAutomation(props) {
    let ref = useRef(undefined);
    let [guidePoint, setGuidePoint] = useState(undefined);
    let [pointBeingDragged, setPointBeingDragged] = useState(undefined);
    let [highlightedPoint, setHighlightedPoint] = useState(undefined);
    let [pointHasDragged, setPointHasDragged] = useState(false);

    const getXY = (e) => {
        let rect = ref.current.getBoundingClientRect();
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        };
    };

    const onClickLine = (e) => {
        e.stopPropagation();
        let state = props.sequencerEngine.store.getState();
        let {x, y} = getXY(e);
        let step = getStepFromPosition(x, props.resolution, 0, state.arrangement.xScale);
        if (isNaN(step)) {
            return;
        }
        let value = getValueFromYPosition(y);
        let event = new SubSequenceVolumeChangeEvent(
            props.subSequence, value);
        event.step = step;
        props.subSequence.mix.insertEvent(event, 1);
        setGuidePoint(undefined);
        setPointBeingDragged(event);
        setPointHasDragged(true);
        props.clearSelection();
    };

    const getValue = () => {
        let state = props.sequencerEngine.store.getState();
        let fade = state.mix.subSequenceFades[props.subSequence.id];
        return fade === undefined ? 1 : fade;
    };

    const getLinePoints = () => {
        let events = props.events || [];
        let pts = []; 
        if (events.length === 0  || events[0].step > 1) {
            let startY = getYPositionFromVolume(getValue()); 
            pts = [{x: 0, y: startY}];
        }
        for (let i in events) {
            pts.push({
                x: getXPositionFromStep(events[i].step, props.resolution, props.xScale),
                y : getYPositionFromVolume(events[i].volume)
            });
        }

        let last = pts[pts.length - 1];

        pts.push({
            x: last.x + 1000,
            y: last.y
        });
        return pts;
    };

    const onMouseMove = useCallback((e) => {
        if (pointBeingDragged === undefined) {
            return;
        }

        let {x,y} = getXY(e);
        let state = props.sequencerEngine.store.getState();
        let step = getStepFromPosition(x, props.resolution, 0, state.arrangement.xScale);
        let value = getValueFromYPosition(y);
        pointBeingDragged.step = step;
        pointBeingDragged.value = value;
        props.subSequence.volumeAutomationMix.updateRedux(
            props.subSequence,
            props.sequencerEngine
        );
        setGuidePoint(undefined);
        setPointHasDragged(true);
        props.setArrangementMouseX(undefined);
        props.setArrangementCursorStep(undefined);
        props.clearSelection();
    }, [pointBeingDragged, props.events, props.resolution]);

    const showGuidePoint = useCallback((e) => {
        let {x,y} = getXY(e);
        let state = props.sequencerEngine.store.getState();
        let step = getStepFromPosition(x, props.resolution, 0, state.arrangement.xScale);
        let value = getValueFromYPosition(y);
        setGuidePoint({step, value});
    }, [setGuidePoint, props.resolution]);

    const onHighlightPoint = useCallback((event) => {
        setHighlightedPoint(event);
        setGuidePoint(undefined);
    }, [setGuidePoint, setHighlightedPoint]);

    const clearState = useCallback((e) => {
        setPointBeingDragged(undefined);
        setHighlightedPoint(undefined);
        setGuidePoint(undefined);
        setPointHasDragged(false);
    }, [setPointBeingDragged, setHighlightedPoint, setGuidePoint]);

    const deleteIfNecessary = useCallback((event) => {
        if (!pointHasDragged) {
            props.subSequence.volumeAutomationMix.deleteEvent(event);
        }
        clearState();
    }, [pointHasDragged]);

    let events = props.events || [];

    let width = getXPositionFromStep(props.subSequence.mix.length + 1000, props.resolution, props.xScale);

    let pts = getLinePoints();
    return (
        <div className="linear-automation-container">
          <svg
            ref={ref}
            width={width}
            height={HEIGHT}
            onMouseLeave={clearState}
            onMouseMove={onMouseMove}
            onMouseUp={clearState}
            >
            <path
              onMouseMove={(e) => showGuidePoint(e)}
              onMouseOver={(e) => showGuidePoint(e)}
              onMouseDown={(e) => onClickLine(e)}
              alpha={0}
              fill="transparent"
              opacity={0}
              strokeWidth={16}
              d={"M 0 0 L " + pts.slice(1).map(({x,y}) => x + ' ' + y).join(' ')}
              stroke={'black'}/>
            <path
              onMouseMove={(e) => showGuidePoint(e)}
              onMouseOver={(e) => showGuidePoint(e)}
              onMouseDown={(e) => onClickLine(e)}
              closed={false}
              fill="transparent"
              strokeWidth={2}
              d={"M 0 0 L " + pts.slice(1).map(({x,y}) => x + ' ' + y).join(' ')}
              stroke={'red'}/>
            <VolumeGuidePoint
              resolution={props.resolution}
              onMouseLeave={() => setGuidePoint(undefined)}
              xScale={props.xScale}
              guidePoint={guidePoint}
              onClick={onClickLine}/>
            {events.map(
                (event, i) => 
                    <VolumeAutomationPoint
                      key={"automation-" + event.id}
                      highlightPoint={() => onHighlightPoint(event)} 
                      unhighlightPoint={() => setHighlightedPoint(undefined)}
                      isHighlighted={event === highlightedPoint || event === pointBeingDragged}
                      onMouseDown={() => setPointBeingDragged(event)}
                      onMouseUp={() => deleteIfNecessary(event)}
                      event={event}
                    />)}
      </svg>
            </div>
    );
}

const mapDispatchToProps = dispatch => {
    return {
        setArrangementMouseX: (mouseX) => dispatch(setArrangementMouseX(mouseX)),
        clearSelection: () => dispatch(setArrangementRowSelections({})),
        setArrangementCursorStep: (x) => dispatch(
            setArrangementCursorStep(x)),
    };
};
 
const mapStateToProps = (state, props) => {
    let mixEvents = state.arrangement.mixEvents[props.subSequence.id];
    let events = mixEvents !== undefined ?
        mixEvents[props.eventType] :
        undefined;
    let fade = state.mix.subSequenceFades[props.subSequence.id];

    return {
        events: events,
        volume: state.mix.isPlayingMix ? undefined :
            fade === undefined ? 1 : fade,
        resolution: DEFAULT_RESOLUTION,
        xScale: state.arrangement.xScale
    };
};

function partitionBy(list) {
   return [list];
}
 
export default connect(mapStateToProps, mapDispatchToProps)(VolumeChangeAutomation);
